import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";

function UserSettings() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@company.com",
    phone: "(555) 123-4567",
    jobTitle: "Property Manager",
    department: "Operations",
    bio: "Experienced property manager with 5+ years in the industry",
    avatar: ""
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    weeklyReports: true,
    maintenanceAlerts: true,
    tenantMessages: true,
    paymentReminders: false,
    systemUpdates: true,
    marketingEmails: false
  });

  const [privacy, setPrivacy] = useState({
    profileVisibility: "team",
    showEmail: false,
    showPhone: false,
    allowDirectMessages: true,
    shareAnalytics: true
  });

  const [security, setSecurity] = useState({
    twoFactorEnabled: false,
    sessionTimeout: "30",
    allowMultipleSessions: true,
    requirePasswordChange: false
  });

  const [display, setDisplay] = useState({
    theme: "light",
    language: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12",
    timezone: "America/New_York",
    density: "comfortable"
  });

  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferenceChange = (preference) => {
    setPreferences(prev => ({
      ...prev,
      [preference]: !prev[preference]
    }));
  };

  const handlePrivacyChange = (field, value) => {
    setPrivacy(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSecurityChange = (field, value) => {
    setSecurity(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDisplayChange = (field, value) => {
    setDisplay(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = () => {
    console.log("Saving user profile:", userProfile);
  };

  const handleSavePreferences = () => {
    console.log("Saving user preferences:", { preferences, privacy, security, display });
  };

  const handlePasswordChange = () => {
    console.log("Opening password change modal");
  };

  const handleEnable2FA = () => {
    if (security.twoFactorEnabled) {
      // Disable 2FA
      setSecurity(prev => ({ ...prev, twoFactorEnabled: false }));
      console.log("Disabling two-factor authentication");
    } else {
      // Navigate to 2FA setup
      navigate("/settings/two-factor-setup");
    }
  };

  return (
    <ArgonBox p={3}>
      <ArgonTypography variant="h4" fontWeight="bold" mb={3}>
        User Settings
      </ArgonTypography>

      {/* Profile Information */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Profile Information
          </ArgonTypography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} display="flex" flexDirection="column" alignItems="center">
              <Avatar
                src={userProfile.avatar}
                sx={{ width: 80, height: 80, mb: 2 }}
              >
                {userProfile.firstName.charAt(0)}{userProfile.lastName.charAt(0)}
              </Avatar>
              <ArgonButton variant="outlined" color="info" size="small">
                Change Photo
              </ArgonButton>
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="First Name"
                    label="First Name"
                    value={userProfile.firstName}
                    onChange={(e) => handleProfileChange('firstName', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="Last Name"
                    label="Last Name"
                    value={userProfile.lastName}
                    onChange={(e) => handleProfileChange('lastName', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="Email"
                    label="Email"
                    value={userProfile.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="Phone"
                    label="Phone"
                    value={userProfile.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="Job Title"
                    label="Job Title"
                    value={userProfile.jobTitle}
                    onChange={(e) => handleProfileChange('jobTitle', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonInput
                    placeholder="Department"
                    label="Department"
                    value={userProfile.department}
                    onChange={(e) => handleProfileChange('department', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <ArgonInput
                    placeholder="Bio"
                    label="Bio"
                    value={userProfile.bio}
                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          
          <ArgonBox display="flex" justifyContent="flex-end" mt={3}>
            <ArgonButton
              variant="gradient"
              color="info"
              onClick={handleSaveProfile}
            >
              Save Profile
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>
      </Card>

      {/* Notification Preferences */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Notification Preferences
          </ArgonTypography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications}
                    onChange={() => handlePreferenceChange('emailNotifications')}
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.smsNotifications}
                    onChange={() => handlePreferenceChange('smsNotifications')}
                  />
                }
                label="SMS Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.pushNotifications}
                    onChange={() => handlePreferenceChange('pushNotifications')}
                  />
                }
                label="Push Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.weeklyReports}
                    onChange={() => handlePreferenceChange('weeklyReports')}
                  />
                }
                label="Weekly Reports"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.maintenanceAlerts}
                    onChange={() => handlePreferenceChange('maintenanceAlerts')}
                  />
                }
                label="Maintenance Alerts"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.tenantMessages}
                    onChange={() => handlePreferenceChange('tenantMessages')}
                  />
                }
                label="Tenant Messages"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.paymentReminders}
                    onChange={() => handlePreferenceChange('paymentReminders')}
                  />
                }
                label="Payment Reminders"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.systemUpdates}
                    onChange={() => handlePreferenceChange('systemUpdates')}
                  />
                }
                label="System Updates"
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Privacy Settings */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Privacy Settings
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Profile Visibility</InputLabel>
                <Select
                  value={privacy.profileVisibility}
                  onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
                  label="Profile Visibility"
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="team">Team Only</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacy.allowDirectMessages}
                    onChange={() => handlePrivacyChange('allowDirectMessages', !privacy.allowDirectMessages)}
                  />
                }
                label="Allow Direct Messages"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacy.showEmail}
                    onChange={() => handlePrivacyChange('showEmail', !privacy.showEmail)}
                  />
                }
                label="Show Email in Profile"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacy.showPhone}
                    onChange={() => handlePrivacyChange('showPhone', !privacy.showPhone)}
                  />
                }
                label="Show Phone in Profile"
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Security Settings */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Security Settings
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ArgonTypography variant="body2">
                  Password
                </ArgonTypography>
                <ArgonButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handlePasswordChange}
                >
                  Change Password
                </ArgonButton>
              </ArgonBox>
            </Grid>
            <Grid item xs={12}>
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <ArgonBox>
                  <ArgonTypography variant="body2">
                    Two-Factor Authentication
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Add an extra layer of security to your account
                  </ArgonTypography>
                </ArgonBox>
                <ArgonButton
                  variant={security.twoFactorEnabled ? "contained" : "outlined"}
                  color={security.twoFactorEnabled ? "success" : "info"}
                  size="small"
                  onClick={handleEnable2FA}
                >
                  {security.twoFactorEnabled ? "Enabled" : "Enable"}
                </ArgonButton>
              </ArgonBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Session Timeout</InputLabel>
                <Select
                  value={security.sessionTimeout}
                  onChange={(e) => handleSecurityChange('sessionTimeout', e.target.value)}
                  label="Session Timeout"
                >
                  <MenuItem value="15">15 minutes</MenuItem>
                  <MenuItem value="30">30 minutes</MenuItem>
                  <MenuItem value="60">1 hour</MenuItem>
                  <MenuItem value="240">4 hours</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={security.allowMultipleSessions}
                    onChange={() => handleSecurityChange('allowMultipleSessions', !security.allowMultipleSessions)}
                  />
                }
                label="Allow Multiple Sessions"
              />
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Display Settings */}
      <Card sx={{ mb: 3 }}>
        <ArgonBox p={3}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
            Display Settings
          </ArgonTypography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={display.theme}
                  onChange={(e) => handleDisplayChange('theme', e.target.value)}
                  label="Theme"
                >
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={display.language}
                  onChange={(e) => handleDisplayChange('language', e.target.value)}
                  label="Language"
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={display.timezone}
                  onChange={(e) => handleDisplayChange('timezone', e.target.value)}
                  label="Timezone"
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Date Format</InputLabel>
                <Select
                  value={display.dateFormat}
                  onChange={(e) => handleDisplayChange('dateFormat', e.target.value)}
                  label="Date Format"
                >
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Time Format</InputLabel>
                <Select
                  value={display.timeFormat}
                  onChange={(e) => handleDisplayChange('timeFormat', e.target.value)}
                  label="Time Format"
                >
                  <MenuItem value="12">12 Hour</MenuItem>
                  <MenuItem value="24">24 Hour</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Density</InputLabel>
                <Select
                  value={display.density}
                  onChange={(e) => handleDisplayChange('density', e.target.value)}
                  label="Density"
                >
                  <MenuItem value="compact">Compact</MenuItem>
                  <MenuItem value="comfortable">Comfortable</MenuItem>
                  <MenuItem value="spacious">Spacious</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      {/* Save Button */}
      <ArgonBox display="flex" justifyContent="flex-end">
        <ArgonButton
          variant="gradient"
          color="info"
          size="large"
          onClick={handleSavePreferences}
          startIcon={<Icon>save</Icon>}
        >
          Save Settings
        </ArgonButton>
      </ArgonBox>
    </ArgonBox>
  );
}

export default UserSettings;